import React, { useState, useMemo } from 'react';
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  IconButton,
  Icon,
  Spinner,
  Center,
  Box,
} from '@chakra-ui/react';
import { MdEdit, MdOutlineDelete } from 'react-icons/md';
import { FaCaretUp, FaCaretDown } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import * as api from '../../../../api/banner';

interface Banner {
  seqConteudo: number;
  nomePublicacao: string;
  datRegistro: string;
  datPublicacao: string;
  datDespublicar: string;
  dscSituacaoPublicacao: string;
}

interface TabelaBannerProps {
  listaBanner: Banner[];
  listaVazia: string;
  loading: boolean;
  onDelete: (seqConteudo: number) => void;
}

type Ordenacao = { chave: keyof Banner; direcao: 'asc' | 'desc' } | null;

const TabelaBanner = ({ 
  listaBanner, 
  listaVazia, 
  loading,
  onDelete
}: TabelaBannerProps) => {
  const navigate = useNavigate();
  const [ordenacao, setOrdenacao] = useState<Ordenacao>(null);
  const [colunaDestacada, setColunaDestacada] = useState<keyof Banner | null>(null);

  function deletarPublicacao(seqPublicacao: number) {
    Swal.fire({
      title: 'Tem certeza?',
      text: "Certeza que deseja deletar esta publicação?",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#1789FC',
      cancelButtonColor: '#EDF2F7',
      confirmButtonText: 'Sim, deletar',
      cancelButtonText: 'Cancelar'
    }).then(async (result) => {
      if (result.isConfirmed) {
        Swal.fire({
          icon: 'info',
          title: 'Aguarde',
          allowOutsideClick: false,
          text: 'Deletando...',
          showConfirmButton: false,
      });
          try {
            const obterBanner = await api.obterBanner(seqPublicacao);
            console.log('obterbanner', obterBanner);
            if(obterBanner.binPublicacao) {
              const delimiter = "/cms-dev-files/";
              const index = obterBanner.binPublicacao.lastIndexOf(delimiter);
              const stringFinalDaUrlCdn =
                index !== -1 ? obterBanner.binPublicacao.substring(index + delimiter.length) : obterBanner.binPublicacao;
              await api.deletarBannerNoCdn(stringFinalDaUrlCdn);
            }
          } catch (error) {
            console.error("Erro ao obter publicação:", error);
          }
          try {
            console.log('seqConteudo', seqPublicacao)
            await api.deletarBanner(seqPublicacao).then(() => {
              onDelete(seqPublicacao);
              Swal.fire({
                icon: 'success',
                title: 'Sucesso!',
                text: 'Publicação deletada com sucesso.',
                confirmButtonColor: '#1789FC',
                confirmButtonText: 'OK',
              });
          });
        } catch (error) {
          console.error('Erro ao deletar publicação:', error);
          Swal.fire({
            icon: 'error',
            title: 'Ocorreu um erro!',
            text: 'Erro ao deletar publicação. Tente novamente mais tarde.',
            confirmButtonColor: '#1789FC',
            confirmButtonText: 'OK',
          }).then(() => {
            navigate(-1)
          });
        }
      }
    });
  }

  const manipularOrdenacao = (chave: keyof Banner) => {
    let direcao: 'asc' | 'desc' = 'asc';
    if (ordenacao && ordenacao.chave === chave && ordenacao.direcao === 'asc') {
      direcao = 'desc';
    }
    setOrdenacao({ chave, direcao });
  };

  const listaOrdenada = useMemo(() => {
    const copia = [...listaBanner];
    if (ordenacao !== null) {
      copia.sort((a, b) => {
        const valorA = a[ordenacao.chave];
        const valorB = b[ordenacao.chave];
        if (typeof valorA === 'string' && typeof valorB === 'string') {
          const comparacao = valorA.localeCompare(valorB);
          return ordenacao.direcao === 'asc' ? comparacao : -comparacao;
        }
        if (valorA < valorB) return ordenacao.direcao === 'asc' ? -1 : 1;
        if (valorA > valorB) return ordenacao.direcao === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return copia;
  }, [listaBanner, ordenacao]);

  const obterIndicadorOrdenacao = (chave: keyof Banner) => {
    if (ordenacao && ordenacao.chave === chave) {
      return ordenacao.direcao === 'asc' ? (
        <FaCaretUp color="#1789FC" style={{ marginLeft: '4px' }} />
      ) : (
        <FaCaretDown color="#1789FC" style={{ marginLeft: '4px' }} />
      );
    }
      return <FaCaretUp color="#4a5568" style={{ marginLeft: '4px' }} />;
  };

  const limitarTexto = (texto: string, tamanhoMaximo: number) => {
    return texto.length > tamanhoMaximo ? texto.substring(0, tamanhoMaximo) + '...' : texto;
  };

  const renderizarLinhas = () =>
    listaOrdenada.length > 0 ? (
      listaOrdenada.map((banner) => (
        <Tr key={banner.seqConteudo}>
          <Td>{limitarTexto(banner.nomePublicacao, 30)}</Td>
          <Td>{banner.dscSituacaoPublicacao}</Td>
          <Td>{banner.datRegistro.split('-').reverse().join('/')}</Td>
          <Td>{banner.datPublicacao.split('-').reverse().join('/')}</Td>
          <Td>{banner.datDespublicar.split('-').reverse().join('/')}</Td>
          <Td>
            <IconButton
              aria-label="Botão Editar"
              bgColor="#fff"
              onClick={() => navigate(`editar-banner/${banner.seqConteudo}`)}
            >
              <Icon as={MdEdit} fontSize="24px" color="#1789FC" />
            </IconButton>
          </Td>
          <Td>
            <IconButton
              aria-label="Botão deletar"
              bgColor="#fff"
              _hover={{ bg: '#ff00002b' }}
              onClick={() => deletarPublicacao(banner.seqConteudo)}
            >
              <Icon as={MdOutlineDelete} fontSize="24px" color="red"/>
            </IconButton>
          </Td>
        </Tr>
      ))
    ) : (
      <Tr>
        <Td colSpan={6} color="#999898" textAlign="center">
          {listaVazia}
        </Td>
      </Tr>
    );

  return (
    <TableContainer>
      <Table variant="simple">
        <Thead>
          <Tr>
            <Th
              _hover={{ transition: '0.1s', color: '#1468C9' }}
              onClick={() => manipularOrdenacao('nomePublicacao')}
              onMouseEnter={() => setColunaDestacada('nomePublicacao')}
              onMouseLeave={() => setColunaDestacada(null)}
              cursor="pointer"
            >
              <Box display="flex" alignItems="center" color={ordenacao?.chave === 'nomePublicacao' ? 'blue.500' : 'inherit'}>
                Título do Banner {obterIndicadorOrdenacao('nomePublicacao')}
              </Box>
            </Th>
            <Th
              _hover={{ transition: '0.1s', color: '#1468C9' }}
              onClick={() => manipularOrdenacao('dscSituacaoPublicacao')}
              onMouseEnter={() => setColunaDestacada('dscSituacaoPublicacao')}
              onMouseLeave={() => setColunaDestacada(null)}
              cursor="pointer"
            >
              <Box display="flex" alignItems="center" color={ordenacao?.chave === 'dscSituacaoPublicacao' ? 'blue.500' : 'inherit'}>
                Situação {obterIndicadorOrdenacao('dscSituacaoPublicacao')}
              </Box>
            </Th>
            <Th
              _hover={{ transition: '0.1s', color: '#1468C9' }}
              onClick={() => manipularOrdenacao('datRegistro')}
              onMouseEnter={() => setColunaDestacada('datRegistro')}
              onMouseLeave={() => setColunaDestacada(null)}
              cursor="pointer"
            >
              <Box display="flex" alignItems="center" color={ordenacao?.chave === 'datRegistro' ? 'blue.500' : 'inherit'}>
                Data do Registro {obterIndicadorOrdenacao('datRegistro')}
              </Box>
            </Th>
            <Th
              _hover={{ transition: '0.1s', color: '#1468C9' }}
              onClick={() => manipularOrdenacao('datPublicacao')}
              onMouseEnter={() => setColunaDestacada('datPublicacao')}
              onMouseLeave={() => setColunaDestacada(null)}
              cursor="pointer"
            >
              <Box display="flex" alignItems="center" color={ordenacao?.chave === 'datPublicacao' ? 'blue.500' : 'inherit'}>
                Data Para Publicação {obterIndicadorOrdenacao('datPublicacao')}
              </Box>
            </Th>
            <Th
              _hover={{ transition: '0.1s', color: '#1468C9' }}
              onClick={() => manipularOrdenacao('datDespublicar')}
              onMouseEnter={() => setColunaDestacada('datDespublicar')}
              onMouseLeave={() => setColunaDestacada(null)}
              cursor="pointer"
            >
              <Box display="flex" alignItems="center" color={ordenacao?.chave === 'datDespublicar' ? 'blue.500' : 'inherit'}>
                Data Fim da Publicação {obterIndicadorOrdenacao('datDespublicar')}
              </Box>
            </Th>
            <Th>Editar</Th>
            <Th>Deletar</Th>
          </Tr>
        </Thead>
        <Tbody>
          {loading ? (
            <Tr>
              <Td colSpan={6}>
                <Center>
                  <Spinner size="lg" color="blue.500" />
                </Center>
              </Td>
            </Tr>
          ) : (
            renderizarLinhas()
          )}
        </Tbody>
      </Table>
    </TableContainer>
  );
};

export default TabelaBanner;
